import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Cpu, Handshake, ShieldCheck } from "lucide-react";
import { ProductFamily } from "@/components/platform/ProductFamily";

const values = [
    { icon: Cpu, title: "Technology first", desc: "AI matching, Talent Passports, and hiring intelligence at the core of everything we build." },
    { icon: Handshake, title: "Human expertise", desc: "Real recruiters layered on top of the platform for the searches that need a human touch." },
    { icon: ShieldCheck, title: "Verified talent", desc: "Hiring should be based on verified talent — not keyword matching." },
];

export default function About() {
    return (
        <div className="bg-background">
            <section className="pt-20 pb-16 lg:pt-28">
                <div className="container-page grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
                    <div className="md:col-span-6">
                        <p className="overline">About Simonara</p>
                        <h1 className="mt-3 font-heading font-semibold text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-foreground leading-[1.05]">
                            We're Building the Future of Hiring.
                        </h1>
                        <p className="mt-6 text-muted-foreground text-lg leading-relaxed">
                            Simonara Talent Solutions combines artificial intelligence with human recruiting expertise
                            to help employers hire faster while giving professionals a smarter way to showcase their skills.
                        </p>
                        <p className="mt-4 text-muted-foreground leading-relaxed">
                            We believe hiring should be based on verified talent — not keyword matching.
                        </p>
                        <div className="mt-8 flex flex-col sm:flex-row gap-3">
                            <Link to="/employers" data-testid="about-cta-employers" className="btn-green">Find Talent <ArrowRight size={16} className="ml-2" /></Link>
                            <Link to="/talent" data-testid="about-cta-professionals" className="btn-outline">Build Your Talent Passport</Link>
                        </div>
                    </div>
                    <div className="md:col-span-6">
                        <img
                            src="https://images.unsplash.com/photo-1622675363311-3e1904dc1885?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
                            alt="Simonara team collaborating on the future of hiring"
                            className="rounded-2xl border border-border shadow-lg w-full object-cover aspect-[4/3]"
                        />
                    </div>
                </div>
            </section>

            <section className="section-pad bg-muted/50">
                <div className="container-page grid grid-cols-1 md:grid-cols-3 gap-6">
                    {values.map((v, i) => (
                        <motion.div
                            key={v.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                            className="rounded-2xl border border-border bg-card p-8"
                        >
                            <div className="w-11 h-11 rounded-xl bg-accent text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
                                <v.icon size={20} />
                            </div>
                            <h3 className="mt-5 font-heading font-semibold text-lg text-foreground">{v.title}</h3>
                            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{v.desc}</p>
                        </motion.div>
                    ))}
                </div>
            </section>

            <ProductFamily />

            <section className="pb-24">
                <div className="container-page">
                    <div className="rounded-3xl bg-brand-navy dark:bg-slate-900 text-white p-10 sm:p-14 flex flex-col sm:flex-row items-center justify-between gap-6">
                        <div>
                            <h2 className="font-heading font-semibold text-2xl sm:text-3xl tracking-tight">Technology first. Recruiting second.</h2>
                            <p className="mt-2 text-white/70">See how the Simonara platform changes the way you hire.</p>
                        </div>
                        <Link to="/pricing" data-testid="about-pricing-cta" className="btn-green shrink-0">View pricing <ArrowRight size={16} className="ml-2" /></Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
